import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Box,
  Text,
  Link,
  useDisclosure,
  useTheme,
  Button,
} from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { ReactComponent as LogOutIcon } from 'assets/svgs/logout.svg';
import { logout } from 'modules/auth/authSlice';
import { FC, useEffect, useCallback } from 'react';
import { GoThreeBars } from 'react-icons/go';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { rgba } from 'polished';
import { RootState } from 'store';
import { mq } from 'styles/breakpoints';
import { rem } from 'utils/fonts';
import { tags, setTag } from 'utils/tags';
import ContextSwitcher from '../ContextSwitcher';
import ProfileSwitcher from '../ProfileSwitcher/ProfileSwitcher';
import { isGuestContextSelector, isCorporateThemeSelector } from 'modules/app/appSlice';

const activeClassName = 'nav-item-active';

const MenuButton = styled(IconButton)`
  font-size: ${rem(32)};
  margin-left: 0;
  position: relative;
  right: -10px;
`;

const List = styled.ul`
  margin: 0;
  padding: 28px 25px;
  list-style: none;
`;

const Item = styled(Box)<{ disabled?: boolean }>`
  display: flex;
  margin: 20px 0;
  height: 100%;
  align-items: center;
  opacity: ${(props): number => (props.disabled ? 0.5 : 1)};
  pointer-events: ${(props): string => (props.disabled ? 'none' : 'auto')};

  &:first-of-type,
  &:last-of-type {
    margin: 0;
  }
`;

const StyledButton = styled(Button)`
  height: 56px;
  font-size: ${rem(16)};
  width: 100%;
  align-items: center;
  background: white;
  border-radius: 100px;
`;

const NavItem = styled(Link)`
  display: flex;
  width: 100%;
  position: relative;
  padding: 1px 0;
  align-items: center;
  opacity: 0.5;
  transition: all 0.2s ease-in-out;
  font-size: ${rem(14)};
  font-weight: normal;

  > div {
    margin-right: 15px;
    position: relative;
    top: -1px;
  }

  &.${activeClassName} {
    opacity: 1;
    font-weight: 500;

    &:before {
      content: '';
      position: absolute;
      left: -25px;
      top: 0;
      height: 100%;
      width: 3px;
    }
  }
`;

const StyledDrawerContent = styled(DrawerContent)`
  max-width: 90vw;
  backface-visibility: hidden;

  &:after {
    content: '';
    position: absolute;
    right: 2px;
    top: 0;
    bottom: 0;
    width: 90vw;
    z-index: 1;
    transform: translate3d(100%, 0, 0);
    pointer-events: none;
  }

  ${mq.sm} {
    max-width: 66vw;

    &:after {
      width: 66vw;
    }
  }
`;

const StyledDrawerBody = styled(DrawerBody)`
  padding: 0 0 30px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  max-height: calc(100% - 82px);

  ${mq.sm} {
    padding: 0 0 40px;
  }
`;

const StyledDrawerCloseButton = styled(DrawerCloseButton)`
  top: 20px;
`;

interface Props {
  menuItems: MenuItem[];
  profileItems: MenuItem[];
  companyItems: MenuItem[];
  contextItems: ContextMenuItem[];
}

const MenuDrawer: FC<Props> = ({ menuItems, profileItems, companyItems, contextItems }) => {
  const { colors } = useTheme();
  const { t } = useTranslation('common');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const context = useSelector((state: RootState) => state.app.context);
  const dispatch = useDispatch();
  const isGuest = useSelector(isGuestContextSelector);
  const location = useLocation();
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  const handleLogout = useCallback(() => {
    dispatch(logout());
  }, []);

  const hideMenu = (): void => {
    onClose();
  };

  const setGoogleTag = (id: string): void => {
    const isNotInOffset = !window.location.pathname.includes('/offset');
    if (id === 'offset' && isNotInOffset) {
      setTag(tags.portalOffset.entry);
    }
  };

  useEffect(() => {
    onClose();
  }, [context, location]);

  return (
    <Box display={['flex', 'flex', 'flex', 'flex', 'none']} alignItems="center" flexGrow={1}>
      <MenuButton
        color="menuButton"
        size="lg"
        variant="ghost"
        aria-label={t('mobileMenu.open')}
        icon={<GoThreeBars />}
        onClick={onOpen}
        w="auto"
        minW="initial"
        p="6px"
        h="50px"
      />
      <Drawer placement="right" onClose={onClose} isOpen={isOpen} size="md">
        <DrawerOverlay>
          <StyledDrawerContent data-testid="drawer-content" sx={{ '&:after': { bg: colors.white } }}>
            <StyledDrawerCloseButton size="lg" color="gray.400" aria-label={t('mobileMenu.close')} />
            <DrawerHeader p="16px 5px 16px 25px" w="calc(100% - 60px)">
              <Flex w="100%">
                <Box w="calc(100% - 70px)">
                  <ContextSwitcher items={contextItems} fullSize current={context} />
                </Box>
              </Flex>
            </DrawerHeader>
            <StyledDrawerBody>
              <Text fontSize={rem(19)} fontWeight={600} color="black" mt="17px" mb="-5px" px="25px">
                {t('mobileMenu.title')}
              </Text>
              <List>
                {menuItems
                  .filter((item: MenuItem) => !item.hideOnMobile)
                  .map((item: MenuItem) => (
                    <Item as="li" key={item.text} disabled={item.disabled}>
                      {item.hasAccess ? (
                        <NavItem
                          as={NavLink}
                          exact
                          to={{ pathname: item.link, search: location.search }}
                          activeClassName={activeClassName}
                          isActive={
                            isGuest
                              ? undefined
                              : //@ts-ignore
                                (_, location): boolean => {
                                  const pathBase = location.pathname.split('/')[1];
                                  const linkBase = item.link.split('/')[1];
                                  return pathBase === linkBase;
                                }
                          }
                          sx={{
                            color: colors.greyLinkActive,
                            '&:hover': {
                              textDecoration: 'none',
                            },
                            '& > div > svg': {
                              stroke: colors.greyLinkActive,
                            },
                            [`&.${activeClassName}`]: {
                              color: colors.pink[500],
                              '&:before': {
                                bg: colors.pink[500],
                              },
                              '& > div > svg': {
                                stroke: colors.pink[500],
                              },
                            },
                          }}
                          onClick={() => {
                            hideMenu();
                            setGoogleTag(item.id);
                          }}
                        >
                          {item.text}
                        </NavItem>
                      ) : (
                        <Box as="span" color={!isCorporateTheme ? rgba(colors.gray[900], 0.2) : rgba(colors.secondary[600], 0.2)}>
                          {item.text}
                        </Box>
                      )}
                    </Item>
                  ))}
              </List>
              <Flex w="100%" pt="5px" borderTop={`1px solid ${rgba(colors.gray[400], 0.3)}`}>
                <ProfileSwitcher profileItems={profileItems} companyItems={companyItems} onItemClick={hideMenu} />
              </Flex>
              <Box px="25px">
                <StyledButton
                  variant="outline"
                  onClick={handleLogout}
                  borderColor={isCorporateTheme ? 'secondary.700' : 'greyLinkActive'}
                  color={isCorporateTheme ? 'secondary.700' : 'greyLinkActive'}
                  fontWeight={isCorporateTheme ? '700' : 'normal'}
                  layerStyle="logOutButton"
                  leftIcon={isCorporateTheme ? <LogOutIcon /> : undefined}
                  rightIcon={!isCorporateTheme ? <LogOutIcon /> : undefined}
                >
                  {t('mobileMenu.logout')}
                </StyledButton>
              </Box>
            </StyledDrawerBody>
          </StyledDrawerContent>
        </DrawerOverlay>
      </Drawer>
    </Box>
  );
};

export default MenuDrawer;
